import { FileInput } from "@components/file-input";
import { LinkInputModal } from "@components/link-input-modal";
import { LinkIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { Badge, Box, Button, Heading, Text } from "@radix-ui/themes";
import { ReactElement, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const FileInputOrLink = ({
  fromImage,
  setFromImage,
  heading,
}: {
  fromImage: File | string | null;
  setFromImage: (arg: File | string | null) => void;
  heading?: string | ReactElement;
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const handleLinkSubmit = (url: string) => {
    setFromImage(url);
    setIsImageLoading(true);
    setImageLoadError(false);
    // Image loading will be handled in the preview component
  };

  return (
    <div className="space-y-2">
      <LinkInputModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleLinkSubmit}
      />

      <div className="flex items-center justify-center">
        {!!heading && (
          <Heading size="3" className="grow">
            {heading}
          </Heading>
        )}
      </div>

      <Box className="w-full rounded-md border border-dashed border-slate-500 cursor-pointer select-none p-2 space-y-2">
        {typeof fromImage === "string" && !imageLoadError && (
          <div onClick={() => {}}>
            <div className="flex flex-row space-x-2 p-2 items-center">
              <img
                src={fromImage}
                className="w-auto h-20 object-cover"
                style={{ maxWidth: "64px" }}
                alt={t("general.files.linked_image")}
                onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const img = e.currentTarget;
                  setImageSize({
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                  });
                  setIsImageLoading(false);
                }}
                onError={() => {
                  console.error("Failed to load image from URL:", fromImage);
                  setImageLoadError(true);
                  setFromImage(null);
                  toast.error(t("general.files.image_load_error"));
                }}
              />
              <Text
                size="3"
                className="block text-left grow overflow-hidden"
                style={{ whiteSpace: "break-spaces", wordBreak: "break-all" }}
              >
                <span className="line-clamp-2">
                  {new URL(fromImage).pathname.split("/").pop() ||
                    t("general.files.linked_image")}
                </span>
                <div className="space-x-1">
                  {imageSize && (
                    <Badge size="1">
                      {imageSize.width}x{imageSize.height}
                    </Badge>
                  )}
                  {isImageLoading && (
                    <Badge size="1">{t("general.files.loading")}</Badge>
                  )}
                </div>
              </Text>
            </div>
          </div>
        )}

        {!fromImage || (typeof fromImage !== "string" && !imageLoadError) ? (
          <FileInput
            value={fromImage as File}
            onChange={setFromImage}
            className={"border-none p-0"}
          />
        ) : null}

        {typeof fromImage !== "string" && !fromImage && false && (
          <Button
            size="1"
            className="w-full"
            onClick={() => setIsModalOpen(true)}
          >
            <LinkIcon className="w-4 h-4" />
            {t("general.files.use_link")}
          </Button>
        )}
        {!!fromImage && (
          <Button
            size="1"
            color="red"
            className="w-full"
            onClick={() => setFromImage(null)}
          >
            <XMarkIcon className="w-4 h-4" />
            {t("general.files.reset")}
          </Button>
        )}
      </Box>
    </div>
  );
};
